/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { message } from 'antd'
import editClientProfile, {
	EditClientWhatsAppDetailsBody,
	EditClientWhatsAppDetailsResponse,
} from 'api/clients/editClientWhatsAppDetails'
import getClientWhatsAppDetails from 'api/clients/getClientWhatsAppDetails'
import { ClientWhatsAppDetails } from 'types'

/**
 * Types for the slice of single client in the store
 */
export interface ClientWhatsAppDetailsState {
	loading: boolean
	updating: boolean
	details: ClientWhatsAppDetails | null
}

/**
 * Initial state of the slice single client
 */
const initialState: ClientWhatsAppDetailsState = {
	loading: false,
	updating: false,
	details: null,
}

/**
 * Action for getting single clients
 *
 * Tries to get single clients from the API and returns the result in the payload
 *
 * @param {string} id - id of the user for single client
 * @returns {Promise<ClientWhatsAppDetails>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getClientWhatsAppDetailsAction = createAsyncThunk<
	ClientWhatsAppDetails,
	string
>(
	'clientWhatsAppDetails/getClientWhatsAppDetails',
	async (id, { rejectWithValue }) => {
		try {
			const response = await getClientWhatsAppDetails(id)
			return response
		} catch (error: any) {
			return rejectWithValue(error.message)
		}
	}
)

export const editClientWhatsAppDetailsAction = createAsyncThunk<
	EditClientWhatsAppDetailsResponse,
	EditClientWhatsAppDetailsBody & { id: string }
>(
	'clientWhatsAppDetails/editProfile',
	async (body, { rejectWithValue, dispatch }) => {
		try {
			const response = await editClientProfile(body.id, body)
			dispatch(getClientWhatsAppDetailsAction(body.id))
			return response
		} catch (error: any) {
			return rejectWithValue(error.message)
		}
	}
)

export const clientWhatsAppDetailsSlice = createSlice({
	name: 'clientWhatsAppDetails',
	initialState,
	reducers: {
		clearClientWhatsAppDetails: () => {
			return initialState
		},
	},
	extraReducers: builder => {
		/** Get Client WhatsApp Details */
		builder.addCase(getClientWhatsAppDetailsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(
			getClientWhatsAppDetailsAction.fulfilled,
			(state, { payload }) => {
				state.loading = false
				state.details = payload
			}
		)
		builder.addCase(getClientWhatsAppDetailsAction.rejected, state => {
			state.loading = false
		})

		/** Edit Client WhatsApp Details */
		builder.addCase(editClientWhatsAppDetailsAction.pending, state => {
			state.updating = true
		})
		builder.addCase(editClientWhatsAppDetailsAction.fulfilled, state => {
			state.updating = false
			message.success('Profile has been changed!')
		})
		builder.addCase(editClientWhatsAppDetailsAction.rejected, state => {
			state.updating = false
		})
	},
})

export const { clearClientWhatsAppDetails } = clientWhatsAppDetailsSlice.actions

export default clientWhatsAppDetailsSlice.reducer
